/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import {styled} from '@mui/material/styles';
import {
  Button,
  ButtonVariant,
} from '@practicegenius/pg-ui/src/base/Material/components/Button';
import {ErrorList} from '../../@practicegenius/react-final-form/effects/handlesInitialErrors';
import {Form} from '../../@practicegenius/react-final-form';
import {BaseScreen, ExtendedButtonProps} from './BaseScreen';
import {ProgressBar} from '../ProgressBar';
import {ErrorMessage} from '../InlineError';
import {FormRenderProps} from '../../@practicegenius/react-final-form/Form';

const InitialErrors = styled(ErrorMessage)({
  margin: 0,
});

const btns = {
  submit: 'submit',
  secondary: 'secondary',
};

export function FormScreen({
  submit,
  submitText = 'Next',
  secondaryButtonProps,
  initialValues,
  initialErrors,
  validate,
  children,
  progress,
  ...otherProps
}: Omit<React.ComponentProps<typeof BaseScreen>, 'children' | 'progress'> & {
  submit: (values: any) => Promise<unknown>;
  submitText: string;
  secondaryButtonProps?: React.ComponentProps<typeof Button>;
  initialValues?: any;
  initialErrors?: ErrorList;
  validate?: (values: any) => any | Promise<any>;
  children: React.ReactNode | ((a: FormRenderProps) => React.ReactNode);
  progress?: number;
}) {
  const [btnClicked, setBtnClicked] = React.useState<null | keyof typeof btns>(
    null,
  );
  return (
    <Form
      initialValues={initialValues}
      initialErrors={initialErrors}
      onSubmit={submit}
      validate={validate}
    >
      {formRP => {
        const {pristine, initialErrors} = formRP;

        const submitButtonProps = {
          'data-testid': 'submit',
          type: 'submit' as const,
          disabled: formRP.submitDisabled,
          children: submitText,
          onClick: () => setBtnClicked(btns.submit as 'submit'),
          loading: btnClicked === btns.submit && formRP.submitting,
          color: 'primary' as const,
        };

        const secondarySubmit =
          secondaryButtonProps && secondaryButtonProps.type === 'submit'
            ? {loading: btnClicked === btns.secondary && formRP.submitting}
            : {};

        const actionButtons = secondaryButtonProps
          ? [
              submitButtonProps,
              {
                color: 'secondary' as const,
                variant: ButtonVariant.outlined as const,
                ...secondaryButtonProps,
                ...secondarySubmit,
                onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                  setBtnClicked(btns.secondary as 'secondary');
                  secondaryButtonProps?.onClick?.(e);
                },
              } as ExtendedButtonProps,
            ]
          : [submitButtonProps];

        return (
          <BaseScreen
            {...otherProps}
            actionButtons={actionButtons}
            divider={progress ? <ProgressBar progress={progress} /> : undefined}
          >
            {typeof children === 'function' ? children(formRP) : children}

            {initialErrors && pristine && (
              <InitialErrors>{formRP.initialErrors}</InitialErrors>
            )}
          </BaseScreen>
        );
      }}
    </Form>
  );
}
